import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { GET_PARTICIPANT, CREATE_PARTICIPANT_OUTREACH, DELETE_PARTICIPANT_OUTREACH } from './queries';
import { toast } from '@/components/ui/toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Phone, Mail, User, MapPin, FileText, X, Eye } from 'lucide-react';

const qaStaffOptions = [
  { value: 'Antony Schreurs', label: 'Antony Schreurs' },
  { value: 'Diana Ramirez', label: 'Diana Ramirez' },
  { value: 'William Prewett', label: 'William Prewett' }
];

const _d = (date:any)=>{
  if (date == null) return '';
  return date.split('T')[0];
}

const contactMethod = ['Email', 'Phone', 'In Person'];
const results = [ 'Rejected to Enroll', 'Agreed to Enroll', 'Waiting Response (First Attempt)', 'Waiting Response (Follow Up Attempt)'];

const OutreachView = ({outreach, refetch, close} : any) => {
  const [deleteParticipantOutreach] = useMutation(DELETE_PARTICIPANT_OUTREACH);

  const handleDelete = (outreachid: number) => {
    if (!window.confirm('Are you sure you want to delete this outreach attempt?'))       
      return;

    deleteParticipantOutreach({variables: {outreachid: outreach.id}});      
    refetch();
    close();
    
  }

  return (
    <Card>
      <CardContent className="pt-6">
      <div className="grid grid-cols-4 gap-4 mb-4 font-bold">          
          <div>Date:</div>
          <div>Contact Method:</div>
          <div>Result</div>
          <div>QA</div>
        </div>
      <div className="grid grid-cols-4 gap-4">          
          <div>{_d(outreach.contactDate)}</div>
          <div>{contactMethod[outreach.contactMethod]}</div>
          <div>{results[outreach.results]}</div>
          <div>{outreach.qaStaff}</div>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4 font-bold">          
          <div>Address:</div>
          <div>City:</div>
          <div>ZIP:</div>
          <div>Email:</div>
        </div>
        <div className="grid grid-cols-4 gap-4">          
          <div>{outreach.address}</div>
          <div>{outreach.city}</div>
          <div>{outreach.zip}</div>
          <div>{outreach.email}</div>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4 font-bold">          
          <div>Phone Number:</div>
          <div>Name of Contact:</div>
        </div>
        <div className="grid grid-cols-4 gap-4">          
          <div>{outreach.phoneNumber}</div>
          <div>{outreach.nameOfContact}</div>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4 font-bold">          
          <div>Notes:</div>
        </div>
        <div>{outreach.notes}</div>
        <div className="mt-8 flex justify-end">
          <Button 
            variant="destructive" 
            onClick={()=>{
              handleDelete(outreach.uniqueid);
            }}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export const Outreach: React.FC = () => {
  const { odrnumber, participant } = useParams<{ odrnumber: any, participant: any }>();

  const { loading, error, data, refetch } = useQuery(GET_PARTICIPANT, {
    variables: { odrnumber : parseInt(odrnumber) }
  });

  const [currentOutReach, setCurrentOutReach] = useState(null);

  const [createParticipantOutreach] = useMutation(CREATE_PARTICIPANT_OUTREACH);

  const [formState, setFormState] = useState({
    contactMethod: '',
    date: '',
    results: '',
    qaStaff: '',
    address: '',
    city: '',
    zip: '',
    email: '',
    phoneNumber: '',
    nameOfContact: '',
    notes: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleSelectChange = (selectedOption: any) => {
    setFormState({ ...formState, qaStaff: selectedOption ? selectedOption.value : '' });
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleSubmit = () => {

    if (formState.date == null || formState.date == '') 
      return toast.error('Please enter a date');

    if (formState.contactMethod == null || formState.contactMethod == '')
      return toast.error('Please select a contact method');

    if (formState.results == null || formState.results == '')
      return toast.error('Please select results');

    if (formState.qaStaff == null || formState.qaStaff == '')
      return toast.error('Please select QA Staff');


    const safeVars = {
      contactMethod: formState.contactMethod || undefined,
      date: formState.date || undefined,
      results: formState.results || undefined,
      qaStaff: formState.qaStaff || undefined,
      address: formState.address|| undefined,
      city: formState.city || undefined,
      zip: formState.zip|| undefined,
      email: formState.email|| undefined,
      phoneNumber: formState.phoneNumber|| undefined,
      nameOfContact: formState.nameOfContact || undefined,
      notes: formState.notes || undefined
    };
    

    createParticipantOutreach({
      variables: {
        odrnumber: odrnumber,
        participant: participant,
        input: safeVars
      }
    }).then(response => {
      console.log('Outreach attempt created', response.data);
      refetch();
      // Handle success or error response
    });

    setFormState({
      contactMethod: '',
      date: '',
      results: '',
      qaStaff: '',
      address: '',
      city: '',
      zip: '',
      email: '',
      phoneNumber: '',
      nameOfContact: '',
      notes: ''
    });
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

 

  return (
    <div>
      <Card className="m-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Create New Outreach Attempt
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="space-y-2">
            <Label htmlFor="date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date
            </Label>
            <Input 
              id="date"
              type="date" 
              name="date" 
              onChange={handleChange} 
              value={formState.date}
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Contact Method
            </Label>
            <Select name="contactMethod" onValueChange={(value) => handleChange({target: {name: 'contactMethod', value}} as any)} value={formState.contactMethod}>
              <SelectTrigger>
                <SelectValue placeholder="Select contact method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Email</SelectItem>
                <SelectItem value="1">Phone</SelectItem>
                <SelectItem value="2">In Person</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Results
            </Label>
            <Select name="results" onValueChange={(value) => handleChange({target: {name: 'results', value}} as any)} value={formState.results}>
              <SelectTrigger>
                <SelectValue placeholder="Select result" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Agreed to Enroll</SelectItem>
                <SelectItem value="0">Rejected to Enroll</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

          <div className="space-y-2 mb-6">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              QA Staff
            </Label>
            <Select onValueChange={(value) => setFormState({ ...formState, qaStaff: value })} value={formState.qaStaff}>
              <SelectTrigger className="w-80">
                <SelectValue placeholder="Select or type a staff member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Antony Schreurs">Antony Schreurs</SelectItem>
                <SelectItem value="Diana Ramirez">Diana Ramirez</SelectItem>
                <SelectItem value="William Prewett">William Prewett</SelectItem>
              </SelectContent>
            </Select>
          </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="space-y-2">
            <Label htmlFor="address" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Address
            </Label>
            <Input 
              id="address"
              type="text" 
              name="address" 
              onChange={handleChange} 
              value={formState.address}
              placeholder="Enter address"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input 
              id="city"
              type="text" 
              name="city" 
              onChange={handleChange} 
              value={formState.city}
              placeholder="Enter city"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zip">ZIP</Label>
            <Input 
              id="zip"
              type="text" 
              name="zip" 
              onChange={handleChange} 
              value={formState.zip}
              placeholder="Enter ZIP code"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Address
            </Label>
            <Input 
              id="email"
              type="email" 
              name="email" 
              onChange={handleChange} 
              value={formState.email}
              placeholder="Enter email address"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone Number
            </Label>
            <Input 
              id="phoneNumber"
              type="tel" 
              name="phoneNumber" 
              onChange={handleChange} 
              value={formState.phoneNumber}
              placeholder="Enter phone number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nameOfContact" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Name of Contact
            </Label>
            <Input 
              id="nameOfContact"
              type="text" 
              name="nameOfContact" 
              onChange={handleChange} 
              value={formState.nameOfContact}
              placeholder="Enter contact name"
            />
          </div>
        </div>

        <div className="space-y-2 mb-6">
          <Label htmlFor="notes" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Notes
          </Label>
          <Textarea 
            id="notes"
            name="notes" 
            onChange={handleTextareaChange} 
            value={formState.notes}
            placeholder="Enter any additional notes..."
            rows={4}
          />
        </div>

        <div className="flex justify-end">
          <Button type="button" onClick={handleSubmit} className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Create Outreach Attempt
          </Button>
        </div>
        </CardContent>
      </Card>


      <Card className="m-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Outreach History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Contact Method</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>QA Staff</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.getParticipantByODRNumber.outreach.map((attempt: any, index: number) => (
                <TableRow 
                  key={index} 
                  className="cursor-pointer hover:bg-[var(--neutral-gray-10)]"
                  onClick={() => setCurrentOutReach(attempt)}
                >
                  <TableCell className="font-medium">{_d(attempt.contactDate)}</TableCell>
                  <TableCell>{contactMethod[attempt.contactMethod]}</TableCell>
                  <TableCell>{results[attempt.results]}</TableCell>
                  <TableCell>{attempt.qaStaff}</TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentOutReach(attempt);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


      <Dialog open={!!currentOutReach} onOpenChange={(open: boolean) => !open && setCurrentOutReach(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Outreach Details
            </DialogTitle>
          </DialogHeader>
          {currentOutReach && (
            <OutreachView outreach={currentOutReach} refetch={refetch} close={()=>{setCurrentOutReach(null)}} />
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

